/*
 * Hero Carousel — Ariel Malhas
 * Carrossel de banner puro (somente imagens). Sem dependências externas.
 * Recursos: loop infinito com clones, autoplay com pausa no hover/foco,
 * swipe em toque, navegação por teclado/setas/indicadores, respeito a
 * prefers-reduced-motion e autoplay apenas quando visível.
 */
(function () {
  'use strict';

  var SWIPE_RATIO = 0.15; // fração da largura para confirmar a troca de slide

  function HeroCarousel(root) {
    this.root = root;
    this.viewport = root.querySelector('[data-hero-viewport]');
    this.track = root.querySelector('[data-hero-track]');
    if (!this.viewport || !this.track) return;

    this.slides = Array.prototype.slice.call(
      this.track.querySelectorAll('[data-hero-slide]')
    );
    this.dots = Array.prototype.slice.call(
      root.querySelectorAll('[data-hero-dot]')
    );
    this.prevBtn = root.querySelector('[data-hero-prev]');
    this.nextBtn = root.querySelector('[data-hero-next]');

    this.count = this.slides.length;
    this.reduceMotion =
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.autoplay = root.getAttribute('data-autoplay') === 'true' && !this.reduceMotion;
    this.interval = parseInt(root.getAttribute('data-interval'), 10) || 6000;
    this.speed = this.reduceMotion
      ? 0
      : parseInt(root.getAttribute('data-speed'), 10) || 450;

    this.timer = null;
    this.isVisible = true;
    this.animating = false;

    // Estado de arraste (swipe)
    this.dragging = false;
    this.startX = 0;
    this.deltaX = 0;
    this.trackWidth = 0;

    if (this.count <= 1) {
      // Slide único: nada a controlar.
      this.setStaticSingle();
      return;
    }

    this.setup();
  }

  HeroCarousel.prototype.setStaticSingle = function () {
    this.track.style.transform = 'translate3d(0,0,0)';
    if (this.prevBtn) this.prevBtn.style.display = 'none';
    if (this.nextBtn) this.nextBtn.style.display = 'none';
  };

  HeroCarousel.prototype.setup = function () {
    var n = this.count;

    // Clones para loop infinito contínuo.
    this.cloneFirst = this.slides[0].cloneNode(true);
    this.cloneLast = this.slides[n - 1].cloneNode(true);
    [this.cloneFirst, this.cloneLast].forEach(function (clone) {
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('data-hero-slide');
      var link = clone.querySelector('a');
      if (link) link.setAttribute('tabindex', '-1');
      // Clones nunca devem carregar com prioridade.
      var img = clone.querySelector('img');
      if (img) {
        img.setAttribute('loading', 'lazy');
        img.removeAttribute('fetchpriority');
      }
    });
    this.track.appendChild(this.cloneFirst);
    this.track.insertBefore(this.cloneLast, this.track.firstChild);

    this.total = n + 2;
    // Cada slide é `flex: 0 0 100%` (uma viewport) e o track tem a largura de
    // uma viewport, então os slides ficam em 0%, 100%, 200%… Para avançar um
    // slide, o track translada 100% (uma viewport inteira) por passo.
    this.step = 100;
    this.domIndex = 1; // primeiro slide real (após o clone inicial)
    this.realIndex = 0;

    this.setPosition(true);
    this.updateDots();
    this.bindEvents();
    if (this.autoplay) this.start();
  };

  // Posiciona o track. instant = sem transição.
  HeroCarousel.prototype.setPosition = function (instant) {
    var offset = this.domIndex * this.step;
    this.track.style.transition = instant
      ? 'none'
      : 'transform ' + this.speed + 'ms cubic-bezier(0.4, 0, 0.2, 1)';
    this.track.style.transform = 'translate3d(-' + offset + '%,0,0)';
    if (instant) {
      // Força reflow para que a próxima transição seja aplicada.
      /* eslint-disable-next-line no-unused-expressions */
      this.track.offsetHeight;
    }
  };

  HeroCarousel.prototype.goTo = function (domIndex) {
    if (this.animating) return;
    this.animating = true;
    this.domIndex = domIndex;
    this.realIndex = ((domIndex - 1) % this.count + this.count) % this.count;
    this.setPosition(false);
    this.updateDots();

    // Se a aba for ocultada durante a transição, o navegador pode nunca
    // disparar transitionend — e "animating" ficaria travado para sempre.
    // Fallback: força o fim da transição pouco depois da duração prevista.
    var self = this;
    window.clearTimeout(this.transitionFallback);
    this.transitionFallback = window.setTimeout(function () {
      if (self.animating) self.onTransitionEnd();
    }, this.speed + 120);
  };

  HeroCarousel.prototype.next = function () {
    this.goTo(this.domIndex + 1);
  };

  HeroCarousel.prototype.prev = function () {
    this.goTo(this.domIndex - 1);
  };

  HeroCarousel.prototype.onTransitionEnd = function () {
    window.clearTimeout(this.transitionFallback);
    this.animating = false;
    // Salto invisível ao chegar em um clone.
    if (this.domIndex === this.total - 1) {
      this.domIndex = 1;
      this.setPosition(true);
    } else if (this.domIndex === 0) {
      this.domIndex = this.count;
      this.setPosition(true);
    }
    this.realIndex =
      ((this.domIndex - 1) % this.count + this.count) % this.count;
    this.updateDots();
  };

  HeroCarousel.prototype.updateDots = function () {
    var active = this.realIndex;
    this.dots.forEach(function (dot, i) {
      var isActive = i === active;
      dot.classList.toggle('is-active', isActive);
      if (isActive) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  };

  // ----- Autoplay -----
  HeroCarousel.prototype.start = function () {
    if (!this.autoplay) return;
    this.stop();
    var self = this;
    this.timer = window.setInterval(function () {
      if (self.isVisible && !self.dragging) self.next();
    }, this.interval);
  };

  HeroCarousel.prototype.stop = function () {
    if (this.timer) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
  };

  // ----- Eventos -----
  HeroCarousel.prototype.bindEvents = function () {
    var self = this;

    this.track.addEventListener('transitionend', function (e) {
      if (e.target === self.track && e.propertyName === 'transform') {
        self.onTransitionEnd();
      }
    });

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', function () {
        self.next();
        self.restart();
      });
    }
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', function () {
        self.prev();
        self.restart();
      });
    }

    this.dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () {
        self.goTo(i + 1);
        self.restart();
      });
    });

    // Pausa no hover e no foco por teclado.
    this.root.addEventListener('mouseenter', function () {
      self.stop();
    });
    this.root.addEventListener('mouseleave', function () {
      self.start();
    });
    this.root.addEventListener('focusin', function () {
      self.stop();
    });
    this.root.addEventListener('focusout', function () {
      if (!self.root.contains(document.activeElement)) self.start();
    });

    // Navegação por teclado (setas).
    this.root.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        self.prev();
        self.restart();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        self.next();
        self.restart();
      }
    });

    // Swipe / arraste com Pointer Events.
    if (window.PointerEvent) {
      this.viewport.addEventListener('pointerdown', function (e) {
        self.onPointerDown(e);
      });
      this.viewport.addEventListener('pointermove', function (e) {
        self.onPointerMove(e);
      });
      this.viewport.addEventListener('pointerup', function (e) {
        self.onPointerUp(e);
      });
      this.viewport.addEventListener('pointercancel', function (e) {
        self.onPointerUp(e);
      });
    }

    // Autoplay apenas quando a seção está visível.
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(
        function (entries) {
          self.isVisible = entries[0].isIntersecting;
        },
        { threshold: 0.25 }
      );
      io.observe(this.root);
    }
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        self.stop();
      } else {
        self.start();
      }
    });

    window.addEventListener('resize', function () {
      self.trackWidth = self.viewport.getBoundingClientRect().width;
    });
  };

  HeroCarousel.prototype.restart = function () {
    if (this.autoplay) this.start();
  };

  // ----- Arraste -----
  HeroCarousel.prototype.onPointerDown = function (e) {
    if (this.animating) return;
    this.dragging = true;
    this.startX = e.clientX;
    this.deltaX = 0;
    this.trackWidth = this.viewport.getBoundingClientRect().width;
    this.track.style.transition = 'none';
    this.stop();
    try {
      this.viewport.setPointerCapture(e.pointerId);
    } catch (err) {
      /* noop */
    }
  };

  HeroCarousel.prototype.onPointerMove = function (e) {
    if (!this.dragging) return;
    this.deltaX = e.clientX - this.startX;
    var base = this.domIndex * this.step;
    // dragPct em % da largura do track (uma viewport), mesma escala do passo.
    var dragPct = (this.deltaX / this.trackWidth) * 100;
    this.track.style.transform =
      'translate3d(' + (-base + dragPct) + '%,0,0)';
  };

  HeroCarousel.prototype.onPointerUp = function (e) {
    if (!this.dragging) return;
    this.dragging = false;
    try {
      this.viewport.releasePointerCapture(e.pointerId);
    } catch (err) {
      /* noop */
    }
    var threshold = this.trackWidth * SWIPE_RATIO;
    if (this.deltaX <= -threshold) {
      this.next();
    } else if (this.deltaX >= threshold) {
      this.prev();
    } else {
      this.setPosition(false); // volta à posição atual
    }
    this.deltaX = 0;
    this.restart();
  };

  // ----- Inicialização -----
  function initAll(context) {
    var scope = context || document;
    var roots = scope.querySelectorAll('[data-hero-carousel]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.__heroCarousel) return;
      root.__heroCarousel = new HeroCarousel(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  // Suporte ao editor de temas do Shopify.
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    var root = e.target.querySelector('[data-hero-carousel]');
    if (root && root.__heroCarousel) {
      root.__heroCarousel.stop();
      root.__heroCarousel = null;
    }
  });
})();
